import React, { useState, useEffect, useRef } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { DEAL_STAGES, EDITABLE_STAGES, PRICE_LOCKED_STAGES, TESTED_ITEM_STATES, getStageName, dealStageLabel, getSearchUrls, parseItems, parseItemLine, displayDealId, parseSellEntry } from '../../utils/dealConstants'
import { buildItemTimeline, describeTimelineRow, formatTimelineWhen, timelineLabel } from '../../utils/itemTimeline'
import { useOutboundPhotos } from '../../hooks/useOutboundPhotos'
import { OutboundPhotoPicker, OutboundPhotoList } from '../shared/OutboundPhotoPicker'
import { useAuth } from '../../hooks/useAuth'
import PinButton from '../shared/PinButton'
import { ConsignmentBadge } from '../shared/ConsignmentBadge.jsx'
import ImageQualityTable from './ImageQualityTable'
import CustomerEmails from './CustomerEmails.jsx'
import CopyButton from '../shared/CopyButton.jsx'
import AppLink from '../shared/AppLink.jsx'
import DealNotes from './DealNotes'
import CustomerMessagesPanel from './quote-assessment/CustomerMessagesPanel.jsx'
import OfferDeclineBanner from './OfferDeclineBanner.jsx'
import { isMoreInfoParked } from './quote-assessment/parkedForReview'
import { isPricedButStale } from './pricingStatus'
import { EngineChips } from './quote-assessment/EngineReadBlock.jsx'
import { mergeItemSignals } from './quote-assessment/mergeItemSignals'
import ReturnRequestDialog from '../warehouse/ReturnRequestDialog.jsx'

// Human labels for the quote `kind` (Prism's three deal kinds). Consignment is
// rendered via ConsignmentBadge instead, so it never reads this map, but it is
// listed for completeness.
const DEAL_KIND_LABELS = {
  trade_in: 'Trade-In',
  part_exchange: 'Part Exchange',
  consignment: 'Consignment',
}

// Payment details apply to the customer (ruling 2, 2026-10-16), for trade-in
// and Commission Service deals only. Part exchange is settled elsewhere and
// never takes a payout instrument. Prism also skips closed and partly paid deals.
const CUSTOMER_PAYOUT_KINDS = ['trade_in', 'consignment']
const EMPTY_PAYMENT_FORM = { method: '', paypalEmail: '', bankName: '', bankAccount: '', bankSort: '' }

/** Whether this deal's payment details save at customer level. */
export function savesPayoutForCustomer(deal) {
  return Boolean(deal?.customer_id) && CUSTOMER_PAYOUT_KINDS.includes(deal?.kind)
}

/** "2 deals: TI-1, TI-7", the deals a customer-level save reaches. */
export function dealCountText(count, references = []) {
  const noun = count === 1 ? 'deal' : 'deals'
  return references.length > 0 ? `${count} ${noun}: ${references.join(', ')}` : `${count} ${noun}`
}

/** The instrument in the shape the customer payout write takes. */
function payoutInstrument(form) {
  const paypal = form.method === 'paypal'
  return {
    method: form.method,
    bank_name: paypal ? null : form.bankName,
    bank_account: paypal ? null : form.bankAccount,
    bank_sort: paypal ? null : form.bankSort,
    paypal_email: paypal ? form.paypalEmail : null,
  }
}

/**
 * `?quote_id=…` for a row that carries one, or '' when it does not.
 *
 * Every grid row already holds the Prism quote_id, so passing it lets the worker
 * skip the lookup that turns a HubSpot deal id back into one — a round trip per
 * endpoint, on a page that opens several. READS ONLY: the worker ignores this on
 * any write, because a hint from the wrong row would mutate the wrong quote and
 * the lookup it replaces is the only thing that would have caught it.
 */
function quoteIdHint(deal) {
  return deal?.quote_id ? `?quote_id=${encodeURIComponent(deal.quote_id)}` : ''
}

/**
 * How a payout-correction link's expiry reads to an operator: the relative
 * distance they think in ("in 7 days") plus the absolute date they can quote
 * back ("22 Sep 2026"). Past expiry it says so plainly; a missing or
 * unparseable expiry returns null, so the caller shows the link without
 * inventing a deadline.
 */
export function payoutExpiryLabel(expiresAtMs, now = Date.now()) {
  if (expiresAtMs == null) return null
  const ms = Number(expiresAtMs)
  if (!Number.isFinite(ms)) return null
  const date = formatDate(new Date(ms))
  if (ms <= now) return `Expired (${date})`
  const days = Math.round((ms - now) / 864e5)
  const rel = days <= 0 ? 'Expires today' : days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`
  return `${rel} (${date})`
}

/**
 * Turn a payout-token failure into a line an operator can act on. The two states
 * the worker relays as themselves — no customer (400/404) and a closed
 * write-gate (503) — get their own wording; anything else shows its own message
 * rather than a generic wall.
 */
export function payoutLinkErrorMessage(error) {
  const status = error?.status
  if (status === 400 || status === 404) {
    return 'No customer record is linked to this deal, so a payout-correction link cannot be generated.'
  }
  if (status === 503) {
    return 'Payout writes are paused right now (write-gate closed). Try again shortly.'
  }
  return error?.message || 'Could not generate a payout-correction link.'
}

/**
 * Why there is no link to show — and the two causes are NOT the same answer.
 *
 * The URL is rebuilt from the customer's collection token, so an absent link
 * means either they hold no token or we could not read the one they hold.
 * Reporting a failed read as "none" invites an operator to regenerate, which
 * rotates the customer's single token and kills every link they already have.
 */
const COLLECTION_LINK_ABSENCE = {
  no_link: { text: 'No collection token for this customer yet — sending the quote issues one.', tone: 'var(--muted)' },
  unknown: { text: 'Could not read this customer\'s collection token, so the link cannot be shown. This is not the same as them having none — reload before regenerating.', tone: 'var(--color-error)' },
}

// Helper to get tracking URL based on carrier
function getTrackingUrl(trackingNumber, carrier) {
  if (!trackingNumber || !carrier) return null
  const carrierLower = carrier.toLowerCase()

  if (carrierLower.includes('royal')) {
    return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
  }
  if (carrierLower.includes('parcel')) {
    if (trackingNumber.toUpperCase().startsWith('PFW')) {
      return `https://send.parcelforce.com/tracking/${trackingNumber.toLowerCase()}`
    }
    return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
  }
  if (carrierLower.includes('fedex')) {
    return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
  }
  if (carrierLower.includes('dhl')) {
    return `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${trackingNumber}`
  }
  return null
}

// SQLite's `datetime('now')` format ("2026-07-12 10:00:00", UTC — how
// condition_assessments stamps assessed_at) is not parseable everywhere:
// Safari yields Invalid Date, and Intl.format on one THROWS mid-render,
// blanking the whole modal. Normalize to ISO and return null for anything
// unparseable rather than crash.
export function formatAssessedDate(value) {
  if (!value) return null
  const raw = String(value)
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw) ? raw.replace(' ', 'T') + 'Z' : raw
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : formatDate(d)
}

// Fallback provenance for HISTORICAL issue items. Pre-cutover testing never
// wrote a condition_assessments row, so the only surviving record is Prism's
// testing detail: outcome + condition + defect notes with the spares offer
// embedded as text by the cutover backfill ("Issue: not powering on\nSpares
// Offer: £15"). Returns the displayable pieces, or null for anything that is
// not an issue outcome — condition mismatches can't be distinguished from
// clean passes in this record, so only issues are surfaced.
export function parseIssueTestingRecord(rec) {
  if (!rec || rec.outcome !== 'issue') return null
  const notes = String(rec.defect_notes || '')
  const spares = notes.match(/Spares Offer:\s*£?([\d,.]+)/i)
  const firstLine = notes.split('\n')[0].replace(/^Issue:\s*/i, '').trim()
  return {
    condition: rec.condition_after || 'Issue',
    reason: firstLine && !/^Spares Offer/i.test(firstLine) ? firstLine : null,
    sparesOffer: spares ? parseFloat(spares[1].replace(/,/g, '')) : null,
    testedAt: rec.tested_at || null,
  }
}

// canRequestReturn: the Payments page sets it, so an unpaid accepted item can be
// marked 'customer wants it back' before it is paid (spec 20261102).
function DealDetailModal({ deal, onClose, onDealUpdated, canRequestReturn = false }) {
  const { user } = useAuth()
  const isAdmin = user?.is_admin

  // Deal data (refreshed from API)
  const [selectedDeal, setSelectedDeal] = useState(deal)

  // Stage changing
  const [changingStage, setChangingStage] = useState(false)

  // Collection date editing
  const [editingCollectionDate, setEditingCollectionDate] = useState(false)
  const [collectionDateValue, setCollectionDateValue] = useState(deal?.collection_date || '')
  const [savingCollectionDate, setSavingCollectionDate] = useState(false)

  // Collection address editing
  const [editingCollectionAddress, setEditingCollectionAddress] = useState(false)
  const [collectionAddressValue, setCollectionAddressValue] = useState(deal?.collection_address || '')
  const [savingCollectionAddress, setSavingCollectionAddress] = useState(false)

  // Collection link — DERIVED from the customer's collection token, not stored,
  // so there is nothing here to edit. Rotating the token is the only write.
  const [regeneratingToken, setRegeneratingToken] = useState(false)

  // Payout-correction link — minted on demand and shown only until the modal
  // closes (the token lives server-side; nothing about it is stored on the
  // deal). The operator generates it, copies it, and pastes it into the ticket.
  const [payoutLink, setPayoutLink] = useState(null)
  const [payoutLinkExpiresAtMs, setPayoutLinkExpiresAtMs] = useState(null)
  const [generatingPayoutLink, setGeneratingPayoutLink] = useState(false)
  const [payoutLinkError, setPayoutLinkError] = useState(null)
  const [payoutLinkCopied, setPayoutLinkCopied] = useState(false)
  // The deal this modal instance is CURRENTLY bound to. The modal is reused
  // across deals (no key={deal.id} at its call sites), so an in-flight mint
  // started on deal A can resolve after a rebind to deal B. A ref, not state,
  // because generatePayoutCorrectionLink closes over the render that started it
  // and must read the live value after its await, not that stale closure.
  const activeDealIdRef = useRef(deal?.id)

  // Pricing log viewing/editing
  const [pricingLogView, setPricingLogView] = useState(null) // 'log' | null
  const [editingPricingLog, setEditingPricingLog] = useState(false)
  const [pricingLogValue, setPricingLogValue] = useState('')
  const [savingPricingLog, setSavingPricingLog] = useState(false)
  const [repricingDeal, setRepricingDeal] = useState(false)
  const [unparking, setUnparking] = useState(false)
  const [repriceToast, setRepriceToast] = useState(null)
  const [paymentDetailsExpanded, setPaymentDetailsExpanded] = useState(false)
  // The deals a customer-level save would reach, from Prism's dry run. Shown
  // before the save so the operator confirms the scope. Null when not asked.
  const [payoutPreview, setPayoutPreview] = useState(null)
  const [addressDetailsExpanded, setAddressDetailsExpanded] = useState(false)
  const [notesExpanded, setNotesExpanded] = useState(false)

  // Prism testing details — feeds the per-item audit timeline (found grade +
  // committed payout) and the tested-lock signal. Purely supplementary: a fetch
  // failure just hides the timeline, never blocks the modal.
  const [testingDetails, setTestingDetails] = useState([])
  // Stream D: the quote's A6 event log, for the per-item audit timeline. Purely
  // supplementary — a fetch failure just hides the timeline, never blocks the modal.
  const [quoteEvents, setQuoteEvents] = useState([])
  const [lineage, setLineage] = useState([])
  // Engine read per item (demand, sell speed, market, viable), the same chips as
  // the verdict note. Supplementary: a failure just leaves the chips out.
  const [diagnosticsItems, setDiagnosticsItems] = useState([])

  // Warehouse test results
  const [testResults, setTestResults] = useState(null)
  const [loadingTestResults, setLoadingTestResults] = useState(false)
  const [testResultsError, setTestResultsError] = useState(null)

  // Created date editing (admin only)
  const [editingCreatedDate, setEditingCreatedDate] = useState(false)
  const [createdDateValue, setCreatedDateValue] = useState('')
  const [savingCreatedDate, setSavingCreatedDate] = useState(false)

  // Item price editing — Quote Assessment interaction: the price is a value you
  // click, not a live input. `priceEdit` holds the one open editor (plus the
  // value it started from), `priceSaving` the one in-flight save.
  const [priceEdit, setPriceEdit] = useState(null)     // { index, field: 'sp'|'bp', value, current }
  const [priceSaving, setPriceSaving] = useState(null) // { index, field }
  const [priceError, setPriceError] = useState(null)
  const [conditionSaving, setConditionSaving] = useState(null) // item position while a grade write is in flight
  const [returningItem, setReturningItem] = useState(null) // { quoteItemId, name } the customer wants back

  // Payment form
  const [settingPayment, setSettingPayment] = useState(false)
  const [paymentForm, setPaymentForm] = useState(EMPTY_PAYMENT_FORM)
  const [savingPayment, setSavingPayment] = useState(false)
  // A preview answers for the details it was asked about. Any edit voids it.
  useEffect(() => { setPayoutPreview(null) }, [paymentForm])

  // The real payout instrument, revealed on demand via H1 preflight — the single
  // audited decrypt site (ADR-0025). The modal otherwise only ever holds the
  // masked mirror (`****4321`, sort absent). Session state only, cleared when the
  // modal switches deals so one deal's instrument never shows under another.
  const [revealedPayout, setRevealedPayout] = useState(null)
  const [revealingPayment, setRevealingPayment] = useState(false)
  const [revealError, setRevealError] = useState(null)

  // Deal images (R2)
  const [dealImages, setDealImages] = useState([])
  const [loadingDealImages, setLoadingDealImages] = useState(false)
  const [dealImagesExpanded, setDealImagesExpanded] = useState(false)

  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, images: [], currentIndex: 0 })

  // Create Ticket modal state
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [ticketDeal, setTicketDeal] = useState(null)
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketMessage, setTicketMessage] = useState('')
  const ticketPhotos = useOutboundPhotos()
  const [sendingTicket, setSendingTicket] = useState(false)

  // AI Rewrite state
  const [rewriteLoading, setRewriteLoading] = useState(false)
  const [showRewriteModal, setShowRewriteModal] = useState(false)
  const [rewrittenText, setRewrittenText] = useState('')

  // Fetch fresh deal data on open
  useEffect(() => {
    if (!deal?.id) return
    // Mark the live deal for in-flight mints started before this switch: their
    // post-await guard compares against this and drops a resolved-too-late link.
    activeDealIdRef.current = deal.id
    setSelectedDeal(deal)
    setPricingLogView(null)
    setPaymentDetailsExpanded(false)
    setPayoutPreview(null)
    setDealImages([])
    setDealImagesExpanded(false)
    // A revealed instrument belongs to ONE deal — never let deal A's real
    // account survive into deal B's masked view.
    setRevealedPayout(null)
    setRevealingPayment(false)
    setRevealError(null)
    // Same rule, and higher stakes: the payout-correction link is scoped to
    // deal A's CUSTOMER. Left standing after a rebind to deal B, an operator
    // could copy A's payment link into B's ticket. Clear all of it on a switch.
    setPayoutLink(null)
    setPayoutLinkExpiresAtMs(null)
    setPayoutLinkError(null)
    setPayoutLinkCopied(false)
    setGeneratingPayoutLink(false)

    async function fetchDeal() {
      try {
        // The row already carries the quote_id, so the worker can skip the
        // lookup that translates a HubSpot deal id into one. Reads only.
        const response = await api.get(`/api/deals/${deal.id}${quoteIdHint(deal)}`)
        if (response.deal) {
          setSelectedDeal(response.deal)
        }
      } catch (e) {
        console.error('Failed to refresh deal:', e)
      }
    }
    fetchDeal()

    // The testing record and event log feed the per-item audit timeline only —
    // both are supplementary, so a failure just leaves the timeline empty rather
    // than alarming or blocking the modal.
    setTestingDetails([])
    setQuoteEvents([])
    setLineage([])
    setDiagnosticsItems([])
    api.get(`/api/deals/${deal.id}/diagnostics`)
      .then(r => setDiagnosticsItems(r.items || []))
      .catch(() => setDiagnosticsItems([]))
    api.get(`/api/deals/${deal.id}/test-results${quoteIdHint(deal)}`)
      .then(r => setTestingDetails(r.results || []))
      .catch(() => setTestingDetails([]))
    api.get(`/api/deals/${deal.id}/events${quoteIdHint(deal)}`)
      .then(r => setQuoteEvents(r.events || []))
      .catch(() => setQuoteEvents([]))
    api.get(`/api/deals/${deal.id}/shopify-lineage${quoteIdHint(deal)}`)
      .then(r => setLineage(r.lineage || []))
      .catch(() => setLineage([]))
  }, [deal?.id])

  // Lightbox functions
  function openLightbox(images, startIndex = 0) {
    setLightbox({ open: true, images, currentIndex: startIndex })
  }

  function closeLightbox() {
    setLightbox({ open: false, images: [], currentIndex: 0 })
  }

  function nextImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }))
  }

  function prevImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }))
  }

  // Lightbox keyboard navigation
  useEffect(() => {
    if (!lightbox.open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowLeft') prevImage()
      if (e.key === 'ArrowRight') nextImage()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox.open])

  // Close modal handler
  function handleClose() {
    setPricingLogView(null)
    setPaymentDetailsExpanded(false)
    setSettingPayment(false)
    setPaymentForm(EMPTY_PAYMENT_FORM)
    setAddressDetailsExpanded(false)
    setDealImages([])
    setDealImagesExpanded(false)
    onClose()
  }

  // Change deal stage
  async function changeDealStage(newStageId) {
    const stageName = getStageName(newStageId)
    if (!confirm(`Change deal stage to "${stageName}"?`)) return

    setChangingStage(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}/status`, { dealstage: newStageId })
      setSelectedDeal(prev => ({ ...prev, dealstage: newStageId }))
      if (onDealUpdated) onDealUpdated()
      alert(`Deal stage changed to "${stageName}"`)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setChangingStage(false)
    }
  }

  async function saveCreatedDate() {
    if (!createdDateValue) {
      alert('Please select a date')
      return
    }
    setSavingCreatedDate(true)
    try {
      // HubSpot createdate expects midnight UTC timestamp in ms
      const timestamp = new Date(createdDateValue + 'T00:00:00Z').getTime()
      await api.patch(`/api/deals/${selectedDeal.id}`, { createdate: String(timestamp) })
      setSelectedDeal(prev => ({ ...prev, createdate: String(timestamp) }))
      setEditingCreatedDate(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingCreatedDate(false)
    }
  }

  async function saveCollectionDate() {
    setSavingCollectionDate(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { collection_date: collectionDateValue || '' })
      setSelectedDeal(prev => ({ ...prev, collection_date: collectionDateValue }))
      setEditingCollectionDate(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingCollectionDate(false)
    }
  }

  async function saveCollectionAddress() {
    setSavingCollectionAddress(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { collection_address: collectionAddressValue })
      setSelectedDeal(prev => ({ ...prev, collection_address: collectionAddressValue }))
      setEditingCollectionAddress(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingCollectionAddress(false)
    }
  }

  async function regenerateCollectionToken() {
    if (!confirm(
      'This issues a NEW collection token for this customer and invalidates every link they already hold — '
      + 'this deal and all their others. Continue?'
    )) return
    setRegeneratingToken(true)
    try {
      const res = await api.post(`/api/deals/${selectedDeal.id}/regenerate-collection-token`)
      setSelectedDeal(prev => ({ ...prev, collection_link: res.collection_link, collection_link_state: 'ready' }))
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setRegeneratingToken(false)
    }
  }

  // Mint a payout-details correction link for this deal's customer. The worker
  // resolves the customer and asks Prism to issue the token; we show the link so
  // the operator can copy it into the ticket. Nothing is stored on the deal.
  async function generatePayoutCorrectionLink() {
    setGeneratingPayoutLink(true)
    setPayoutLinkError(null)
    setPayoutLinkCopied(false)
    // Pin the deal this mint is FOR. `selectedDeal.id` is the starting render's
    // closure value; after the await we compare it to the live ref, not to
    // `selectedDeal` (still stale in this closure). If the modal rebound to
    // another deal mid-flight, drop the result — this link is scoped to this
    // deal's customer and must never land under a different deal.
    const forDeal = selectedDeal.id
    try {
      const res = await api.post(`/api/deals/${forDeal}/payout-token`)
      if (activeDealIdRef.current !== forDeal) return
      setPayoutLink(res.payout_link)
      setPayoutLinkExpiresAtMs(res.expires_at_ms ?? null)
    } catch (error) {
      if (activeDealIdRef.current !== forDeal) return
      setPayoutLink(null)
      setPayoutLinkExpiresAtMs(null)
      setPayoutLinkError(payoutLinkErrorMessage(error))
    } finally {
      if (activeDealIdRef.current === forDeal) setGeneratingPayoutLink(false)
    }
  }

  async function copyPayoutLink() {
    if (!payoutLink) return
    try {
      await navigator.clipboard.writeText(payoutLink)
      setPayoutLinkCopied(true)
      setTimeout(() => setPayoutLinkCopied(false), 2000)
    } catch {
      // Clipboard blocked (insecure context or denied permission): the URL is
      // still on screen, so tell the operator to select and copy it by hand
      // rather than fail silently.
      setPayoutLinkError('Could not copy automatically — select the link above and copy it.')
    }
  }

  async function loadTestResults() {
    setLoadingTestResults(true)
    setTestResultsError(null)
    try {
      const res = await api.get(`/api/deals/${selectedDeal.id}/test-results${quoteIdHint(selectedDeal)}`)
      // One structured record per item (Prism D4).
      setTestResults({ results: res.results || null })
    } catch (error) {
      setTestResultsError(error.message)
    } finally {
      setLoadingTestResults(false)
    }
  }

  async function savePricingLog() {
    setSavingPricingLog(true)
    try {
      const result = await api.put(`/api/deals/${selectedDeal.id}/pricing-log`, { claude_pricing_log: pricingLogValue })
      setSelectedDeal(prev => ({ ...prev, claude_pricing_log: pricingLogValue, pricing_log_status: result.status }))
      setEditingPricingLog(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error saving pricing log: ${error.message}`)
    } finally {
      setSavingPricingLog(false)
    }
  }

  async function forceReprice() {
    setRepricingDeal(true)
    try {
      const result = await api.put(`/api/deals/${selectedDeal.id}/pricing-log`, { claude_pricing_log: '' })
      setSelectedDeal(prev => ({ ...prev, claude_pricing_log: '', pricing_log_status: result.status }))
      setPricingLogView(null)
      setRepriceToast({ message: 'Re-queued for pricing', type: 'success' })
      setTimeout(() => setRepriceToast(null), 3000)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      setRepriceToast({ message: `Error: ${error.message}`, type: 'error' })
      setTimeout(() => setRepriceToast(null), 4000)
    } finally {
      setRepricingDeal(false)
    }
  }

  // D1 'pending' on a quote Prism has fully priced — nothing will ever pick it up again
  // (see isPricedButStale). Offers Mark priced (keep the prices, fix the label) and
  // re-enables Force Re-price, which 'pending' otherwise disables as "Already queued".
  const pricedButStale = isPricedButStale(selectedDeal, parseItems(selectedDeal?.parsed_items).map(parseItemLine))
  const queuedForPricing = selectedDeal?.pricing_log_status === 'pending' && !pricedButStale

  async function markPriced() {
    setRepricingDeal(true)
    try {
      const result = await api.post(`/api/deals/${selectedDeal.id}/mark-priced`, { quote_id: selectedDeal.quote_id })
      if (result?.ok === false) {
        setRepriceToast({ message: result.error || 'Mark priced failed', type: 'error' })
        setTimeout(() => setRepriceToast(null), 6000)
        return
      }
      setSelectedDeal(prev => ({ ...prev, pricing_log_status: 'completed' }))
      setRepriceToast({ message: 'Marked as priced', type: 'success' })
      setTimeout(() => setRepriceToast(null), 3000)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      setRepriceToast({ message: `Error: ${error.message}`, type: 'error' })
      setTimeout(() => setRepriceToast(null), 6000)
    } finally {
      setRepricingDeal(false)
    }
  }

  // Customer replied to a more-info request — clear the park so the quote returns to the
  // assessment queue. Non-destructive: prices are untouched (unlike Re-run pricing).
  async function clearMoreInfoPark() {
    setUnparking(true)
    try {
      await api.post(`/api/deals/${selectedDeal.id}/clear-more-info-park`)
      setSelectedDeal(prev => ({ ...prev, excluded_from_pricing_queue: false, excluded_reason: null }))
      setRepriceToast({ message: 'Returned to Quote Assessment', type: 'success' })
      setTimeout(() => setRepriceToast(null), 3000)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      setRepriceToast({ message: `Error: ${error.message}`, type: 'error' })
      setTimeout(() => setRepriceToast(null), 4000)
    } finally {
      setUnparking(false)
    }
  }

  // Open create ticket modal
  function openTicketModal(targetDeal, e) {
    if (e) e.stopPropagation()
    const d = targetDeal || selectedDeal
    const customerEmail = d?.seller_email || d?.contact_email
    if (!customerEmail) {
      alert('No email address found for this deal')
      return
    }
    setTicketDeal(d)
    setTicketSubject(`Re: ${d?.dealname || 'Your Deal'}`)
    setTicketMessage('')
    ticketPhotos.reset()
    setShowTicketModal(true)
  }

  // Send ticket/email
  async function sendTicket() {
    const customerEmail = ticketDeal?.seller_email || ticketDeal?.contact_email
    if (!customerEmail || !ticketSubject || !ticketMessage) {
      alert('Please fill in subject and message')
      return
    }

    setSendingTicket(true)
    try {
      const form = new FormData()
      form.append('dealId', ticketDeal.id)
      form.append('toEmail', customerEmail)
      form.append('toName', ticketDeal.customer_name || '')
      form.append('subject', ticketSubject)
      form.append('message', ticketMessage)
      ticketPhotos.files.forEach(file => form.append('files', file))
      await api.postForm('/api/tickets/create-from-deal', form)
      alert('Email sent and ticket created')
      setShowTicketModal(false)
      setTicketDeal(null)
      setTicketSubject('')
      setTicketMessage('')
      ticketPhotos.reset()
    } catch (error) {
      alert(`Failed to send: ${error.message}`)
    } finally {
      setSendingTicket(false)
    }
  }

  // AI Rewrite functions
  async function getRewrite() {
    if (!ticketDeal || !ticketMessage.trim()) {
      alert('Please enter some text to rewrite.')
      return
    }
    setRewriteLoading(true)
    try {
      const response = await api.post('/api/tickets/rewrite-draft', {
        text: ticketMessage,
        subject: ticketSubject,
        customerEmail: ticketDeal.seller_email || ticketDeal.contact_email,
        customerName: ticketDeal.customer_name || '',
        dealId: ticketDeal.id
      })
      if (response.rewrittenText) {
        setRewrittenText(response.rewrittenText)
        setShowRewriteModal(true)
      } else {
        alert(response.message || 'Could not rewrite text.')
      }
    } catch (error) {
      alert(`Failed to rewrite: ${error.message}`)
    } finally {
      setRewriteLoading(false)
    }
  }

  function acceptRewrite() {
    setTicketMessage(rewrittenText)
    setShowRewriteModal(false)
    setRewrittenText('')
  }

  function cancelRewrite() {
    setShowRewriteModal(false)
    setRewrittenText('')
  }

  // Reveal the real payout instrument (H1 preflight — the single audited decrypt
  // site, ADR-0025). Returns the details so the edit path can prefill from the
  // same call instead of decrypting twice. Loud on failure: a silent one would
  // leave the mask on screen reading as "these ARE the details".
  async function revealPayment() {
    if (revealedPayout) return revealedPayout
    setRevealingPayment(true)
    setRevealError(null)
    try {
      const res = await api.get(`/api/deals/${selectedDeal.id}/payment-preflight`)
      const details = res.payout_details || {}
      setRevealedPayout(details)
      return details
    } catch (error) {
      setRevealError(`Could not read the payment details: ${error.message}`)
      return null
    } finally {
      setRevealingPayment(false)
    }
  }

  // Enter the edit form. Reveal-then-edit: never prefill from the masked mirror
  // (`****4321`) — a save would write the mask back over the real encrypted
  // account. Setting a fresh method has nothing to reveal; editing an existing
  // one reveals first and prefills from the real values, and if the reveal fails
  // we stay OUT of edit mode rather than open the form on a mask.
  async function startEditingPayment(currentMethod) {
    if (!currentMethod) {
      setPaymentForm({ method: '', paypalEmail: '', bankName: '', bankAccount: '', bankSort: '' })
      setSettingPayment(true)
      return
    }
    const details = revealedPayout || await revealPayment()
    if (!details) return
    if (details.paypal_email) {
      setPaymentForm({ method: 'paypal', paypalEmail: details.paypal_email, bankName: '', bankAccount: '', bankSort: '' })
    } else {
      setPaymentForm({
        method: 'bank',
        paypalEmail: '',
        bankName: details.bank_name || selectedDeal.payment_bank_name || '',
        bankAccount: details.bank_account || '',
        bankSort: details.bank_sort || '',
      })
    }
    setSettingPayment(true)
  }

  // Save payment details: at customer level where the deal allows it (preview
  // first, then confirm), otherwise on this deal only.
  async function savePaymentDetails() {
    if (!paymentFormIsSaveable()) return
    if (savesPayoutForCustomer(selectedDeal)) {
      await previewCustomerPayout()
      return
    }
    await saveDealPayment()
  }

  function paymentFormIsSaveable() {
    if (!paymentForm.method) {
      alert('Please select a payment method')
      return false
    }
    if (paymentForm.method === 'paypal' && !paymentForm.paypalEmail) {
      alert('Please enter PayPal email address')
      return false
    }
    if (paymentForm.method === 'bank' && (!paymentForm.bankName || !paymentForm.bankAccount || !paymentForm.bankSort)) {
      alert('Please fill in all bank details')
      return false
    }

    // Defence in depth against the mask-overwrite: a value still carrying '*' is
    // the masked mirror, not something the operator typed. Saving it would
    // replace the real encrypted instrument with an unpayable mask. The backend
    // rejects this too (payoutFromProperties), but block here so the operator
    // sees why rather than a 502.
    if ([paymentForm.paypalEmail, paymentForm.bankAccount, paymentForm.bankSort].some(v => (v || '').includes('*'))) {
      alert('These details are masked (they contain “*”). Click “Show payment details” to reveal the real values before saving — saving a masked value would overwrite the customer’s real account.')
      return false
    }
    return true
  }

  // Ask Prism which deals a customer-level save would reach, writing nothing.
  // An API that does not know the dry run refuses the fields (422) or answers
  // without `dry_run: true`; then the customer-wide write is never used, and
  // the save stays on this deal, as before.
  async function previewCustomerPayout() {
    const forDeal = selectedDeal.id
    setSavingPayment(true)
    try {
      const preview = await api.put(`/api/customers/${selectedDeal.customer_id}/payout`, {
        ...payoutInstrument(paymentForm), kinds: CUSTOMER_PAYOUT_KINDS, dry_run: true,
      })
      if (activeDealIdRef.current !== forDeal) return
      if (preview?.dry_run !== true) {
        await saveDealPayment()
        return
      }
      setPayoutPreview({ count: preview.quotes_updated, references: preview.quote_references || [] })
    } catch (error) {
      if (activeDealIdRef.current !== forDeal) return
      if (error.status === 422) {
        await saveDealPayment()
        return
      }
      alert(`Error saving payment details: ${error.message}`)
    } finally {
      setSavingPayment(false)
    }
  }

  // The operator confirmed the preview: store it for the customer, which
  // copies it to their trade-in and Commission Service deals still to be paid.
  async function saveCustomerPayout() {
    const forDeal = selectedDeal.id
    setSavingPayment(true)
    try {
      const result = await api.put(`/api/customers/${selectedDeal.customer_id}/payout`, {
        ...payoutInstrument(paymentForm), kinds: CUSTOMER_PAYOUT_KINDS, dry_run: false,
      })
      if (activeDealIdRef.current !== forDeal) return
      showSavedPayment()
      alert(`Payment details saved to ${dealCountText(result.quotes_updated, result.quote_references || [])}`)
    } catch (error) {
      if (activeDealIdRef.current !== forDeal) return
      alert(`Error saving payment details: ${error.message}`)
    } finally {
      setSavingPayment(false)
    }
  }

  // This deal only: a deal with no customer, a part exchange, or a Prism that
  // cannot scope the customer-level write.
  async function saveDealPayment() {
    setSavingPayment(true)
    try {
      const updateData = {}
      if (paymentForm.method === 'paypal') {
        updateData.payment_paypal_email = paymentForm.paypalEmail
        updateData.payment_bank_name = ''
        updateData.payment_bank_account = ''
        updateData.payment_bank_sort = ''
      } else {
        updateData.payment_bank_name = paymentForm.bankName
        updateData.payment_bank_account = paymentForm.bankAccount
        updateData.payment_bank_sort = paymentForm.bankSort
        updateData.payment_paypal_email = ''
      }

      await api.patch(`/api/deals/${selectedDeal.id}`, updateData)
      showSavedPayment()
      alert('Payment details saved')
    } catch (error) {
      alert(`Error saving payment details: ${error.message}`)
    } finally {
      setSavingPayment(false)
    }
  }

  // Show what was just saved and close the form.
  function showSavedPayment() {
    const newDeal = {
      ...selectedDeal,
      payment_method: paymentForm.method,
      payment_details: paymentForm.method === 'paypal'
        ? paymentForm.paypalEmail
        : { name: paymentForm.bankName, account: paymentForm.bankAccount, sortCode: paymentForm.bankSort }
    }
    setSelectedDeal(newDeal)
    // The operator just typed these real values (now stored encrypted), so hold
    // them as the revealed instrument — the display keeps showing what was
    // saved rather than reverting to the mask until the next modal reload.
    setRevealedPayout(paymentForm.method === 'paypal'
      ? { paypal_email: paymentForm.paypalEmail }
      : { bank_name: paymentForm.bankName, bank_account: paymentForm.bankAccount, bank_sort: paymentForm.bankSort })

    setSettingPayment(false)
    setPayoutPreview(null)
    setPaymentForm(EMPTY_PAYMENT_FORM)
    if (onDealUpdated) onDealUpdated()
  }

  // Load deal images from R2
  async function loadDealImages() {
    if (!selectedDeal?.id) return

    setLoadingDealImages(true)
    try {
      const result = await api.get(`/api/deals/${selectedDeal.id}/images`)
      setDealImages(result.images || [])
    } catch (error) {
      console.error('Error loading images:', error)
    } finally {
      setLoadingDealImages(false)
    }
  }

  // Toggle deal images
  function toggleDealImages() {
    if (dealImagesExpanded) {
      setDealImagesExpanded(false)
    } else {
      setDealImagesExpanded(true)
      if (dealImages.length === 0) {
        loadDealImages()
      }
    }
  }

  // Guard against typo overpayments (same rule as Quote Assessment): confirm
  // any increase of more than 10% from the current value.
  function confirmBigIncrease(field, oldRaw, newVal) {
    const oldVal = Number(oldRaw)
    if (!Number.isFinite(oldVal) || oldVal <= 0) return true   // no meaningful % vs a blank/zero value
    if (newVal <= oldVal * 1.1) return true
    const pct = Math.round(((newVal - oldVal) / oldVal) * 100)
    return confirm(`You are increasing ${field} by ${pct}% (+£${(newVal - oldVal).toFixed(2)}, from £${oldVal.toFixed(2)} to £${newVal.toFixed(2)}). Are you sure?`)
  }

  // Save the open price editor.
  //
  // Prices are per-ITEM in Prism, held as a tier against the item's condition.
  // This used to rewrite the whole `parsed_items` blob and PATCH it onto the
  // deal, which the property router refuses outright — a blob write cannot be
  // reconciled to item edits — so every price edit here failed and was lost.
  //
  // The item is addressed by its STORED index, read from `selectedDeal.items`.
  // Position is not usable: removing an item rejects rather than deletes it, so
  // the survivors keep their original indexes while their positions shift.
  async function commitPriceEdit(itemIndex, field) {
    // Escape closes the editor before blur fires; a commit for an editor that
    // is no longer open must do nothing.
    if (!priceEdit || priceEdit.index !== itemIndex || priceEdit.field !== field) return
    const { value, current } = priceEdit

    // The Sell box also accepts a pair ("100/41") — the only way to price an
    // item that has no price yet, whose first tier needs both halves at once.
    const entry = field === 'sp' ? parseSellEntry(value) : null
    const pairBp = entry?.bp ?? null

    // Blank or unparseable is NOT zero. `parseFloat(value) || 0` would turn a
    // cleared box into a posted £0 — silent data loss on a money field. Leave
    // the value alone instead.
    const numValue = field === 'sp' ? (entry?.sp ?? NaN) : parseFloat(value)
    if (!Number.isFinite(numValue) || numValue < 0) { setPriceEdit(null); return }
    // An edit that changed nothing must not write. Prices are stored as a whole
    // tier read-merge-write, so a redundant save is a real round trip and a real
    // chance to clobber a concurrent edit. A pair always writes: its BP half is
    // new even when the SP matches.
    if (pairBp == null && Number.isFinite(current) && numValue === current) { setPriceEdit(null); return }

    // Buying above the selling price is a guaranteed loss — refuse with the
    // real reason. `sp` was captured from the same parsed line when the editor
    // opened. Editor stays open with the typed value so the operator corrects
    // rather than retypes (same posture as the slow-fetch guard below).
    if (field === 'bp' && Number.isFinite(priceEdit.sp) && numValue > priceEdit.sp) {
      setPriceError(`BP £${numValue.toFixed(2)} is above the SP £${priceEdit.sp.toFixed(2)} — we'd pay more than we plan to sell it for. Lower the BP, or raise the SP first.`)
      return
    }
    if (pairBp != null && pairBp > numValue) {
      setPriceError(`BP £${pairBp.toFixed(2)} is above the SP £${numValue.toFixed(2)} — we'd pay more than we plan to sell it for.`)
      return
    }

    // This guard runs BEFORE the editor closes: on a slow deal fetch the
    // operator keeps their typed value and can retry, instead of watching it
    // vanish alongside the error.
    const stored = selectedDeal?.items?.[itemIndex]?.idx
    if (!Number.isInteger(stored)) {
      setPriceError('This deal is still loading its item list, so a price edit could land on the wrong item. Give it a moment and press Enter again.')
      return
    }

    if (!confirmBigIncrease(field.toUpperCase(), current, numValue)) { setPriceEdit(null); return }
    setPriceEdit(null)

    const path = field === 'sp' ? 'sell-price' : 'buy-price'
    const body = field === 'sp'
      ? (pairBp != null ? { sp: numValue, bp: pairBp } : { sp: numValue })
      : { bp: numValue }

    setPriceSaving({ index: itemIndex, field })
    setPriceError(null)
    try {
      const r = await api.post(`/api/deals/${selectedDeal.id}/items/${stored}/${path}`, body)
      if (r.parsedItems) setSelectedDeal(prev => ({ ...prev, parsed_items: r.parsedItems }))
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      setPriceError(`Failed to update ${field.toUpperCase()}: ${error.message}`)
    } finally {
      setPriceSaving(null)
    }
  }

  // Set an item's grade — allowed ONLY while the item has none. An existing
  // grade is authoritative (testing / assessment review) and is not editable
  // from this modal. Needed because a price has no tier to sit in until the
  // item is graded: BP/SP edits on an Ungraded item are refused by Prism.
  async function saveItemCondition(itemIndex, condition) {
    const stored = selectedDeal?.items?.[itemIndex]?.idx
    if (!Number.isInteger(stored)) {
      setPriceError('This deal is still loading its item list, so the grade could land on the wrong item. Give it a moment and try again.')
      return
    }
    setConditionSaving(itemIndex)
    setPriceError(null)
    try {
      // Response fields travel together (blob + quote condition + projections)
      // and must not drift — same contract the assessment page renders from.
      const r = await api.patch(`/api/deals/${selectedDeal.id}/items/${stored}`, { condition })
      setSelectedDeal(prev => ({
        ...prev,
        parsed_items: r.parsedItems ?? prev.parsed_items,
        condition: r.condition ?? prev.condition,
        items: r.items ?? prev.items,
      }))
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      setPriceError(`Failed to set condition: ${error.message}`)
    } finally {
      setConditionSaving(null)
    }
  }

  // One price box (Sell or Buy) with the Quote Assessment interaction: the
  // price renders as a clickable value; clicking opens an inline input that
  // saves on Enter/blur and cancels on Escape.
  function renderPriceBox(item, index, field, isEditable) {
    const label = field === 'sp' ? 'Sell' : 'Buy'
    const color = field === 'sp' ? 'var(--color-success)' : 'var(--color-warning)'
    const current = field === 'sp' ? item.sp : item.bp
    const isOpen = priceEdit?.index === index && priceEdit?.field === field
    const isSaving = priceSaving?.index === index && priceSaving?.field === field
    return (
      <div style={{
        background: 'var(--color-bg)',
        padding: '6px 12px',
        borderRadius: 6,
        border: '1px solid var(--color-border)',
        textAlign: 'center',
        minWidth: 70
      }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{label}</div>
        {isOpen ? (
          <input
            // The Sell box takes text: it accepts the "100/41" pair, which a
            // number input's keystroke filter would swallow the "/" of.
            type={field === 'sp' ? 'text' : 'number'}
            inputMode="decimal"
            // Words, not example numbers: a numeric placeholder reads as a
            // prefilled value, and Enter on the "prefilled" empty box silently
            // closes the editor — exactly "my save did nothing".
            placeholder={field === 'sp' && item.sp == null && item.bp == null ? 'Sell/Buy' : undefined}
            autoFocus
            value={priceEdit.value}
            onChange={e => setPriceEdit(prev => prev && { ...prev, value: e.target.value })}
            onBlur={() => commitPriceEdit(index, field)}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setPriceEdit(null) }}
            style={{
              width: 70, textAlign: 'center', fontWeight: 600,
              color, background: 'transparent',
              border: '1px solid var(--color-border)', borderRadius: 4,
              padding: '2px 4px', fontSize: '0.85rem'
            }}
          />
        ) : (
          <div
            onClick={isEditable && !isSaving ? () => { setPriceError(null); setPriceEdit({ index, field, value: current != null ? String(current) : '', current, sp: item.sp }) } : undefined}
            title={isEditable ? 'Click to edit' : undefined}
            style={{
              fontWeight: 600, color,
              cursor: isEditable ? 'pointer' : 'default',
              textDecoration: isEditable ? 'underline dotted' : 'none'
            }}
          >
            {isSaving ? '…' : `£${current?.toFixed(2) || '0.00'}`}
          </div>
        )}
      </div>
    )
  }

  // Render parsed items for modal with structured display
  function renderModalParsedItems() {
    // Prices are editable on any OPEN deal (canonical Prism stages like
    // `expired` count as open — a revived quote is still negotiable); only
    // closed deals lock. Other modal edits keep the narrower EDITABLE_STAGES.
    const isEditable = !PRICE_LOCKED_STAGES.includes(selectedDeal?.dealstage ?? selectedDeal?.stage)
    const items = parseItems(selectedDeal?.parsed_items)
    // Rejected/returned items, shown greyed and read-only. Without them a
    // closed-lost deal reads as "No items" — the cutover mapped every item on
    // a lost deal to `rejected`, so the whole offer was invisible.
    const deadItems = selectedDeal?.dead_items || []
    if (items.length === 0 && deadItems.length === 0) return <span className="text-muted">No items</span>
    const itemSignals = mergeItemSignals(selectedDeal?.items, diagnosticsItems)

    return (
      <div style={{ fontSize: '0.85rem' }}>
        {priceError && (
          <div style={{ color: 'var(--color-error)', marginBottom: 8 }}>{priceError}</div>
        )}
        {items.map((itemLine, index) => {
          const item = parseItemLine(itemLine)
          const urls = getSearchUrls(itemLine)
          // The item's OWN grade. The blob omits the grade when it is Ungraded,
          // and the projection says 'Ungraded' explicitly — both mean "no grade",
          // which is the state the picker exists for.
          const projectedCondition = selectedDeal?.items?.[index]?.condition
          const itemGrade = item?.condition
            || (projectedCondition && projectedCondition !== 'Ungraded' ? projectedCondition : null)
          const quoteItemId = selectedDeal?.items?.[index]?.quote_item_id

          // Stream D (spec_20260808): one signal — does the item have a testing
          // record? — flips the buy price from the quoted offer to the committed
          // payout AND locks price editing. `buy_price_cents` on the structured
          // item is committed-else-offer (cents); the blob BP is the Valuation
          // and is only shown before testing. Both SP and BP lock post-test;
          // sell-side/listing repricing lives on the pricing surface, not here.
          const structuredItem = selectedDeal?.items?.[index]
          // Robust "has been tested" signal: the item STATE rides on A4, so a
          // transient /test-results failure cannot unlock a tested item (C-2).
          // The testing fetch only supplements it (and feeds the timeline).
          const hasTestingRecord = TESTED_ITEM_STATES.includes(structuredItem?.state)
            || (!!quoteItemId && testingDetails.some(r => r.quote_item_id === quoteItemId))
          const committedBpPounds = structuredItem?.buy_price_cents != null
            ? structuredItem.buy_price_cents / 100
            : null
          const itemEditable = isEditable && !hasTestingRecord

          const itemTimeline = buildItemTimeline({
            quoteItemId,
            item: structuredItem,
            events: quoteEvents,
            lineage,
            testingRecord: testingDetails.find(r => r.quote_item_id === quoteItemId) || null,
            createdate: selectedDeal?.createdate,
          })

          if (!item) return null

          return (
            <div key={index} style={{
              marginBottom: 12,
              padding: 12,
              background: 'var(--color-bg-secondary)',
              borderRadius: 8,
              border: '1px solid var(--color-border)'
            }}>
              {/* Item Header */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 8
              }}>
                <span style={{
                  background: 'var(--color-primary)',
                  color: 'var(--color-charcoal)',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {index + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.name}</div>
                  {structuredItem?.state === 'return_requested' && (
                    <div style={{ color: 'var(--color-warning)', fontSize: '0.8rem', fontWeight: 600 }}>Return requested</div>
                  )}
                  {item.details && (
                    <div style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{item.details}</div>
                  )}
                </div>
              </div>

              {/* Condition + Qty, SP, BP boxes */}
              <div style={{
                display: 'flex',
                gap: 8,
                marginBottom: 8,
                flexWrap: 'wrap'
              }}>
                <div style={{
                  background: 'var(--color-bg)',
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  textAlign: 'center',
                  minWidth: 70
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Condition</div>
                  {/* A set grade is authoritative and read-only here; an
                      ungraded item gets a picker, because prices have no tier
                      to sit in until it is graded. */}
                  {conditionSaving === index ? (
                    <div style={{ fontWeight: 600 }}>…</div>
                  ) : itemGrade ? (
                    <div style={{ fontWeight: 600 }}>{itemGrade}</div>
                  ) : isEditable ? (
                    <select
                      defaultValue=""
                      onChange={e => e.target.value && saveItemCondition(index, e.target.value)}
                      style={{
                        fontWeight: 600, background: 'transparent',
                        border: '1px solid var(--color-warning)', borderRadius: 4,
                        padding: '2px 4px', fontSize: '0.85rem', color: 'var(--color-warning)'
                      }}
                    >
                      <option value="" disabled>Set…</option>
                      <option value="Mint">Like New</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Very Good">Very Good</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Broken">Spares or Repair</option>
                    </select>
                  ) : (
                    <div style={{ fontWeight: 600, color: 'var(--color-text-muted)' }}>—</div>
                  )}
                </div>
                <div style={{
                  background: 'var(--color-bg)',
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  textAlign: 'center',
                  minWidth: 50
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Qty</div>
                  <div style={{ fontWeight: 600 }}>{item.qty || 1}</div>
                </div>
                {renderPriceBox(item, index, 'sp', itemEditable)}
                {renderPriceBox(
                  hasTestingRecord && committedBpPounds != null ? { ...item, bp: committedBpPounds } : item,
                  index, 'bp', itemEditable
                )}
              </div>

              {/* Item audit timeline (Stream D): the quoted -> received -> tested
                  -> paid story, one row per event, keyed to this item. Shown once
                  there is more than the bare "Quoted" row to tell. */}
              {itemTimeline.length > 1 && (
                <div style={{ marginBottom: 8, fontSize: '0.78rem' }}>
                  <div style={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', fontSize: '0.68rem', marginBottom: 4 }}>
                    Item history
                  </div>
                  {itemTimeline.map((r, ri) => (
                    <div key={ri} style={{
                      display: 'flex', gap: 8, padding: '2px 0 2px 8px',
                      borderLeft: '2px solid var(--color-border)'
                    }}>
                      <span style={{ minWidth: 64, fontWeight: 600 }}>{timelineLabel(r) || r.key}</span>
                      <span style={{ flex: 1, color: 'var(--color-text-muted)' }}>{describeTimelineRow(r)}</span>
                      <span style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{formatTimelineWhen(r.at)}</span>
                    </div>
                  ))}
                </div>
              )}

              {canRequestReturn && quoteItemId && selectedDeal.quote_id && selectedDeal.kind !== 'consignment'
                && structuredItem?.state === 'tested_ok' && !structuredItem?.payment_id && (
                <div style={{ marginBottom: 8 }}>
                  <button
                    className="btn btn-xs btn-secondary"
                    onClick={() => setReturningItem({ quoteItemId, name: item.name })}
                  >
                    Customer wants it back
                  </button>
                </div>
              )}

              {/* Search buttons */}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.reverbSold, '_blank')} title="Reverb Sold">RS</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.reverbActive, '_blank')} title="Reverb Active">RA</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.ebayActive, '_blank')} title="eBay Active">EA</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.ebaySold, '_blank')} title="eBay Sold">ES</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.google, '_blank')} title="Google">G</button>
                {itemSignals[index] && <EngineChips signals={itemSignals[index]} />}
              </div>
            </div>
          )
        })}

        {/* Dead items: what the customer offered that was declined or sent
            back. Read-only by design — there is nothing operational to do
            with a rejected item, only to know it existed. */}
        {deadItems.map((dead, i) => (
          <div key={`dead-${i}`} style={{
            marginBottom: 12,
            padding: 12,
            background: 'var(--color-bg-secondary)',
            borderRadius: 8,
            border: '1px dashed var(--color-border)',
            opacity: 0.65
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                background: 'var(--color-bg)',
                color: 'var(--color-text-muted)',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                flexShrink: 0
              }}>
                {dead.state === 'returned' ? 'Returned' : 'Rejected'}
              </span>
              <span style={{ fontWeight: 600 }}>{dead.name}</span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                {dead.condition && dead.condition !== 'Ungraded' ? dead.condition : ''}
                {dead.quantity > 1 ? ` · Qty ${dead.quantity}` : ''}
              </span>
            </div>
            {dead.rejection_reason && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 4 }}>{dead.rejection_reason}</div>
            )}
          </div>
        ))}
      </div>
    )
  }

  if (!selectedDeal) return null

  return (
    <>
      {/* Deal Detail Modal */}
      <div className="modal-overlay">
        <div
          className="modal-content modal-large"
          onClick={e => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflow: 'auto' }}
        >
          <div className="modal-header">
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>Deal Name</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>{selectedDeal.dealname}</h2>
                {/* Consignment is a per-quote fact carried on the deal envelope as
                    `kind`; every other item surface shows this chip, the deal modal
                    was the gap. Deal-level, not per-item: the item blob has no
                    per-item ownership flag, and a consignment quote is consignment
                    whole. Split is intentionally not passed — it is not finalised
                    until the offer and is absent from this read (badge says so). */}
                {selectedDeal.kind === 'consignment' && <ConsignmentBadge />}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <PinButton
                entityType="deal"
                entityId={selectedDeal.id}
                entityName={selectedDeal.dealname || selectedDeal.customer_name}
              />
              <button className="modal-close" onClick={handleClose}>&times;</button>
            </div>
          </div>
          <div className="modal-body-scroll">
            {/* Waiting-on-customer park: hidden from both queues until un-parked here.
                Nothing clears it automatically, so this button is the way back once the
                customer replies — non-destructive, prices intact. */}
            {isMoreInfoParked(selectedDeal) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap',
                            background: 'var(--color-warning-bg, #fff8e1)', border: '1px solid var(--color-warning, #f0b429)',
                            borderRadius: 8, padding: '10px 14px', marginBottom: 'var(--space-4)' }}>
                <span style={{ fontSize: '0.85rem' }}>
                  ⏳ <strong>Parked — waiting on customer</strong> (more info requested). It is hidden from
                  Quote Assessment until you return it.
                </span>
                <button className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }}
                        disabled={unparking} onClick={clearMoreInfoPark}
                        title="Customer has replied — clear the park and put this quote back in Quote Assessment (prices unchanged)">
                  {unparking ? 'Returning…' : 'Customer replied → return to Assessment'}
                </button>
              </div>
            )}
            <OfferDeclineBanner declines={selectedDeal.offer_declines} stage={selectedDeal.stage} />
            {/* Deal info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
              {/* Left column */}
              <div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Deal ID</label>
                  {/* A copy button, not a link: this modal already is the deal. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>{displayDealId(selectedDeal)}</span>
                    {displayDealId(selectedDeal) && (
                      <CopyButton value={displayDealId(selectedDeal)} label="Copy deal ID" toastMessage="Deal ID copied" />
                    )}
                  </div>
                </div>
                {/* The deal kind, for every known kind. Consignment keeps the
                    purple chip (its tooltip explains the sale-time payout);
                    trade-in / part-exchange get a neutral badge. Hidden only when
                    kind is absent — legacy rows that predate the field, where a
                    guessed "Trade-In" would be a claim we cannot stand behind. */}
                {selectedDeal.kind && (
                  <div style={{ marginBottom: 'var(--space-3)' }}>
                    <label className="text-muted" style={{ fontSize: '0.75rem' }}>Type</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      {selectedDeal.kind === 'consignment'
                        ? <ConsignmentBadge />
                        : <span className="badge badge-neutral">{DEAL_KIND_LABELS[selectedDeal.kind] || selectedDeal.kind}</span>}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Customer</label>
                  <div>{selectedDeal.customer_name || '-'}</div>
                  {(selectedDeal.seller_email || selectedDeal.contact_email) && (
                    <CustomerEmails email={selectedDeal.seller_email || selectedDeal.contact_email} />
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {/* All Deals matches the deal email or the customer email. */}
                    {(selectedDeal.seller_email || selectedDeal.contact_email) && (
                      <AppLink
                        href={`/deals/all?email=${encodeURIComponent(selectedDeal.seller_email || selectedDeal.contact_email)}`}
                        onFollow={onClose}
                        className="btn btn-sm btn-secondary"
                      >
                        View all deals
                      </AppLink>
                    )}
                    {/* Create Ticket button */}
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => openTicketModal(selectedDeal)}
                    >
                      📧 Create Ticket
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Created</label>
                  {isAdmin && editingCreatedDate ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <input
                        type="date"
                        className="form-input"
                        value={createdDateValue}
                        onChange={e => setCreatedDateValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveCreatedDate(); if (e.key === 'Escape') setEditingCreatedDate(false) }}
                        autoFocus
                        style={{ width: '160px' }}
                      />
                      <button className="btn btn-sm btn-primary" onClick={saveCreatedDate} disabled={savingCreatedDate}>
                        {savingCreatedDate ? '...' : 'Save'}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditingCreatedDate(false)} disabled={savingCreatedDate}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <span>{formatDate(selectedDeal.createdate)}</span>
                      {isAdmin && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            const d = selectedDeal.createdate ? new Date(Number(selectedDeal.createdate) || selectedDeal.createdate) : new Date()
                            setCreatedDateValue(d.toISOString().split('T')[0])
                            setEditingCreatedDate(true)
                          }}
                          title="Edit created date"
                          style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Total Buy Price</label>
                  {/* Stream D (spec_20260808): the total we PAY — the sum of
                      each item's committed-else-offer buy price (structured
                      `buy_price_cents`, committed once tested, else the quoted
                      offer), NOT the Valuation blob. Stays live as pre-test BPs
                      are edited, because those edits refresh the item. */}
                  <div>
                    <span>{formatCurrency(
                      (selectedDeal.items || []).reduce(
                        (sum, it) => sum + (it.buy_price_cents ?? 0) * (it.quantity ?? 1), 0
                      ) / 100
                    )}</span>
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Stage</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <select
                      className="form-input"
                      value={selectedDeal.dealstage ?? selectedDeal.stage ?? ''}
                      onChange={e => changeDealStage(e.target.value)}
                      disabled={changingStage}
                      style={{ flex: 1 }}
                    >
                      {/* Where the quote actually is, when HubSpot has no id for
                          it. Without this the value matched no option and the
                          browser fell back to the first one, so an expired quote
                          read "Quote Requested". Disabled: it still renders as
                          the selected value, but expiry is a scheduler
                          transition with no HubSpot id to write, so offering it
                          would be a button that cannot work. The legal
                          destinations below stay selectable — Prism allows
                          expired -> accepted, because late replies happen. */}
                      {!selectedDeal.dealstage && selectedDeal.stage && (
                        <option value={selectedDeal.stage} disabled>{dealStageLabel(selectedDeal)}</option>
                      )}
                      {DEAL_STAGES.filter(s => s.id).map(stage => (
                        <option key={stage.id} value={stage.id}>{stage.label}</option>
                      ))}
                    </select>
                    {changingStage && <div className="loading-spinner loading-spinner-sm" />}
                  </div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Date</label>
                  {editingCollectionDate ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                      <input
                        type="date"
                        className="form-input"
                        value={collectionDateValue}
                        onChange={e => setCollectionDateValue(e.target.value)}
                        style={{ flex: 1, fontSize: '0.85rem' }}
                      />
                      <button className="btn btn-primary" onClick={saveCollectionDate} disabled={savingCollectionDate} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                        {savingCollectionDate ? '...' : 'Save'}
                      </button>
                      <button className="btn" onClick={() => { setEditingCollectionDate(false); setCollectionDateValue(selectedDeal.collection_date || '') }} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{selectedDeal.collection_date ? formatDate(selectedDeal.collection_date) : '-'}</span>
                      {isAdmin && (
                        <button onClick={() => { setCollectionDateValue(selectedDeal.collection_date || ''); setEditingCollectionDate(true) }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted)', fontSize: '0.75rem' }} title="Edit collection date">
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Tracking</label>
                  <div>
                    {selectedDeal.tracking_number ? (
                      <>
                        <span>{selectedDeal.carrier || ''} </span>
                        {selectedDeal.tracking_number.split(',').map((tn, idx) => {
                          const trimmed = tn.trim()
                          if (!trimmed) return null
                          const url = getTrackingUrl(trimmed, selectedDeal.carrier)
                          return (
                            <span key={idx}>
                              {idx > 0 && ', '}
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'var(--color-info)' }}
                                >
                                  {trimmed}
                                </a>
                              ) : (
                                <span>{trimmed}</span>
                              )}
                            </span>
                          )
                        })}
                      </>
                    ) : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details - Collapsible */}
            <div style={{
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setPaymentDetailsExpanded(!paymentDetailsExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                <span>Payment Details</span>
                <span style={{
                  transform: paymentDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>▼</span>
              </button>
              {paymentDetailsExpanded && (() => {
                const hasPayPal = selectedDeal.payment_paypal_email && selectedDeal.payment_paypal_email.trim() !== ''
                const hasBank = selectedDeal.payment_bank_account && selectedDeal.payment_bank_account.trim() !== ''
                const paymentMethod = hasPayPal ? 'paypal' : hasBank ? 'bank' : null

                // Prefer the revealed real instrument; fall back to the masked
                // mirror the deal read carries. `revealedPayout` is only ever set
                // by an audited H1 reveal (or the operator's own just-saved edit).
                const paypalDisplay = revealedPayout?.paypal_email || selectedDeal.payment_paypal_email
                const bankNameDisplay = revealedPayout?.bank_name || selectedDeal.payment_bank_name || '-'
                const accountDisplay = revealedPayout?.bank_account || selectedDeal.payment_bank_account || '-'
                const sortDisplay = revealedPayout?.bank_sort || selectedDeal.payment_bank_sort || '-'

                return (
                <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                    <div>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>Payment Method</label>
                      <div style={{ marginTop: 4 }}>
                        {paymentMethod === 'paypal' && (
                          <span className="badge badge-info">PayPal</span>
                        )}
                        {paymentMethod === 'bank' && (
                          <span className="badge badge-secondary">Bank Transfer</span>
                        )}
                        {!paymentMethod && (
                          <span className="badge badge-warning">Not Set</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>Payment Status</label>
                      <div style={{ marginTop: 4 }}>
                        {selectedDeal.payment_status === 'paid' ? (
                          <span className="badge badge-success">Paid</span>
                        ) : selectedDeal.payment_status === 'pending' ? (
                          <span className="badge badge-warning">Pending</span>
                        ) : (
                          <span className="badge">Awaiting</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Payment Details based on method */}
                  {paymentMethod === 'paypal' && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>PayPal Email</label>
                      <div style={{
                        marginTop: 4,
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 6,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem'
                      }}>
                        {paypalDisplay}
                      </div>
                    </div>
                  )}
                  {paymentMethod === 'bank' && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>Bank Details</label>
                      <div style={{
                        marginTop: 4,
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 6,
                        fontSize: '0.85rem'
                      }}>
                        <div style={{ marginBottom: 4 }}><strong>Name:</strong> {bankNameDisplay}</div>
                        <div style={{ marginBottom: 4 }}><strong>Account:</strong> {accountDisplay}</div>
                        <div><strong>Sort Code:</strong> {sortDisplay}</div>
                      </div>
                    </div>
                  )}
                  {!settingPayment && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      {!paymentMethod && (
                        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginBottom: 'var(--space-3)' }}>
                          No payment method has been set for this deal.
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Show payment details — the ONLY way to see the real
                            instrument (H1 preflight, audited). Hidden once
                            revealed. */}
                        {paymentMethod && !revealedPayout && (
                          <button
                            className="btn btn-sm btn-secondary"
                            disabled={revealingPayment}
                            onClick={revealPayment}
                          >
                            {revealingPayment ? 'Revealing…' : 'Show payment details'}
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${paymentMethod ? 'btn-secondary' : 'btn-primary'}`}
                          disabled={revealingPayment}
                          onClick={() => startEditingPayment(paymentMethod)}
                        >
                          {paymentMethod ? 'Edit Payment Details' : 'Set Payment Method'}
                        </button>
                      </div>
                      {revealError && (
                        <div style={{
                          marginTop: 'var(--space-2)',
                          padding: 'var(--space-2) var(--space-3)',
                          background: 'var(--color-error-bg, #fef2f2)',
                          borderRadius: 6,
                          fontSize: '0.8rem',
                          color: 'var(--color-error, #b91c1c)'
                        }}>
                          <strong>{revealError}</strong> The details above stay masked — nothing here is payable until a reveal succeeds.
                        </div>
                      )}
                    </div>
                  )}
                  {/* Payment Setup Form */}
                  {settingPayment && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div style={{ marginBottom: 'var(--space-3)' }}>
                        <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                          Select Payment Method
                        </label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button
                            className={`btn btn-sm ${paymentForm.method === 'paypal' ? 'btn-info' : 'btn-secondary'}`}
                            onClick={() => setPaymentForm(prev => ({ ...prev, method: 'paypal' }))}
                            style={paymentForm.method === 'paypal' ? { background: '#0070ba' } : {}}
                          >
                            PayPal
                          </button>
                          <button
                            className={`btn btn-sm ${paymentForm.method === 'bank' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setPaymentForm(prev => ({ ...prev, method: 'bank' }))}
                          >
                            Bank Transfer
                          </button>
                        </div>
                      </div>

                      {/* PayPal Form */}
                      {paymentForm.method === 'paypal' && (
                        <div style={{ marginBottom: 'var(--space-3)' }}>
                          <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                            PayPal Email Address
                          </label>
                          <input
                            type="email"
                            className="form-input"
                            placeholder="customer@email.com"
                            value={paymentForm.paypalEmail}
                            onChange={e => setPaymentForm(prev => ({ ...prev, paypalEmail: e.target.value }))}
                            style={{ maxWidth: 300 }}
                          />
                        </div>
                      )}

                      {/* Bank Form */}
                      {paymentForm.method === 'bank' && (
                        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                              Account Holder Name
                            </label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="John Smith"
                              value={paymentForm.bankName}
                              onChange={e => setPaymentForm(prev => ({ ...prev, bankName: e.target.value }))}
                              style={{ maxWidth: 300 }}
                            />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', maxWidth: 300 }}>
                            <div>
                              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                                Sort Code
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                placeholder="12-34-56"
                                value={paymentForm.bankSort}
                                onChange={e => setPaymentForm(prev => ({ ...prev, bankSort: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                                Account Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                placeholder="12345678"
                                value={paymentForm.bankAccount}
                                onChange={e => setPaymentForm(prev => ({ ...prev, bankAccount: e.target.value }))}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Customer-level save: the deals it reaches, confirmed before the write */}
                      {paymentForm.method && payoutPreview && (
                        <div style={{ marginTop: 'var(--space-3)' }}>
                          <div style={{ fontSize: '0.85rem', marginBottom: 'var(--space-2)' }}>
                            This will update {dealCountText(payoutPreview.count, payoutPreview.references)}
                          </div>
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => setPayoutPreview(null)} disabled={savingPayment}>
                              Back
                            </button>
                            <button className="btn btn-success btn-sm" onClick={saveCustomerPayout} disabled={savingPayment}>
                              {savingPayment ? 'Saving...' : `Save to ${dealCountText(payoutPreview.count)}`}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {paymentForm.method && !payoutPreview && (
                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setSettingPayment(false)
                              setPaymentForm({ method: '', paypalEmail: '', bankName: '', bankAccount: '', bankSort: '' })
                            }}
                            disabled={savingPayment}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={savePaymentDetails}
                            disabled={savingPayment}
                          >
                            {savingPayment ? 'Saving...' : 'Save Payment Details'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )})()}
            </div>

            {/* Collection Details - Collapsible */}
            <div style={{
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setAddressDetailsExpanded(!addressDetailsExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                <span>Collection Details</span>
                <span style={{
                  transform: addressDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>▼</span>
              </button>
              {addressDetailsExpanded && (() => {
                const hasAddress = selectedDeal.collection_address && selectedDeal.collection_address.trim() !== ''
                const hasPhone = selectedDeal.collection_phone && selectedDeal.collection_phone.trim() !== ''
                const hasSendType = selectedDeal.send_type && selectedDeal.send_type.trim() !== ''
                const hasPackageDetails = selectedDeal.package_details && selectedDeal.package_details.trim() !== ''
                const hasCollectionDate = selectedDeal.collection_date
                const hasAnyDetails = hasAddress || hasPhone || hasSendType || hasPackageDetails || hasCollectionDate

                return (
                  <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg)' }}>
                    {(hasAnyDetails || isAdmin) ? (
                      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                        {/* Send Type & Collection Date Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Send Type</label>
                            <div style={{ marginTop: 4 }}>
                              {hasSendType ? (
                                <span className={`badge ${selectedDeal.send_type === 'courier' ? 'badge-info' : 'badge-secondary'}`}>
                                  {selectedDeal.send_type === 'courier' ? 'Collection' : selectedDeal.send_type === 'dropoff' ? 'Drop Off' : selectedDeal.send_type}
                                </span>
                              ) : (
                                <span className="badge badge-warning">Not Set</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Date</label>
                            <div style={{ marginTop: 4, fontSize: '0.85rem' }}>
                              {editingCollectionDate ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <input type="date" className="form-input" value={collectionDateValue} onChange={e => setCollectionDateValue(e.target.value)} style={{ padding: '4px 8px', fontSize: '0.85rem', width: 160 }} />
                                  <button className="btn btn-primary" onClick={saveCollectionDate} disabled={savingCollectionDate} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                    {savingCollectionDate ? '...' : 'Save'}
                                  </button>
                                  <button className="btn" onClick={() => { setEditingCollectionDate(false); setCollectionDateValue(selectedDeal.collection_date || '') }} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span>{hasCollectionDate ? formatDate(selectedDeal.collection_date) : '-'}</span>
                                  {isAdmin && (
                                    <button onClick={() => { setCollectionDateValue(selectedDeal.collection_date || ''); setEditingCollectionDate(true) }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted)', fontSize: '0.75rem' }} title="Edit collection date">
                                      Edit
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Address */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Address</label>
                            {!editingCollectionAddress && isAdmin && (
                              <button
                                onClick={() => { setCollectionAddressValue(selectedDeal.collection_address || ''); setEditingCollectionAddress(true) }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted)', fontSize: '0.75rem' }}
                                title="Edit collection address"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                          {editingCollectionAddress ? (
                            <div style={{ marginTop: 4 }}>
                              <textarea
                                className="form-input"
                                value={collectionAddressValue}
                                onChange={e => setCollectionAddressValue(e.target.value)}
                                rows={3}
                                style={{ width: '100%', padding: '6px 8px', fontSize: '0.85rem', resize: 'vertical' }}
                              />
                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <button className="btn btn-primary" onClick={saveCollectionAddress} disabled={savingCollectionAddress} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                  {savingCollectionAddress ? 'Saving…' : 'Save'}
                                </button>
                                <button className="btn" onClick={() => { setEditingCollectionAddress(false); setCollectionAddressValue(selectedDeal.collection_address || '') }} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              marginTop: 4,
                              padding: 'var(--space-2) var(--space-3)',
                              background: 'var(--color-bg-secondary)',
                              borderRadius: 6,
                              fontSize: '0.85rem',
                              whiteSpace: 'pre-wrap',
                              color: hasAddress ? 'inherit' : 'var(--muted)',
                              fontStyle: hasAddress ? 'normal' : 'italic',
                            }}>
                              {hasAddress ? selectedDeal.collection_address : 'No collection address set'}
                            </div>
                          )}
                        </div>
                        {/* Phone */}
                        {hasPhone && (
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Phone Number</label>
                            <div style={{
                              marginTop: 4,
                              padding: 'var(--space-2) var(--space-3)',
                              background: 'var(--color-bg-secondary)',
                              borderRadius: 6,
                              fontSize: '0.85rem'
                            }}>
                              <a href={`tel:${selectedDeal.collection_phone}`} style={{ color: 'var(--color-info)' }}>
                                {selectedDeal.collection_phone}
                              </a>
                            </div>
                          </div>
                        )}
                        {/* Package Details */}
                        {hasPackageDetails && (
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Package Details</label>
                            <div style={{
                              marginTop: 4,
                              padding: 'var(--space-2) var(--space-3)',
                              background: 'var(--color-bg-secondary)',
                              borderRadius: 6,
                              fontSize: '0.85rem',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {selectedDeal.package_details}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                        No collection details have been set for this deal.
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>

            {/* Notes - Collapsible */}
            <div style={{
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setNotesExpanded(!notesExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                <span>Show Notes</span>
                <span style={{
                  transform: notesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>▼</span>
              </button>
              {notesExpanded && (
                <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg)' }}>
                  <DealNotes dealId={selectedDeal.id} />
                </div>
              )}
            </div>

            {/* The conversation with the seller, and the reply box */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <CustomerMessagesPanel
                dealId={selectedDeal.id}
                quoteId={selectedDeal.quote_id}
                items={(selectedDeal.items || []).map(i => ({ quote_item_id: i.quote_item_id, name: i.display_name || i.declared_name }))}
              />
            </div>

            {/* Parsed Items */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              {/* Prices are editable inline below; identity, quantity and adding
                  or removing lines happen on Quote Assessment.

                  There used to be an "Edit" button here opening the raw item text
                  in a textarea. It saved by PATCHing the whole `parsed_items`
                  blob, which the property router refuses — items are individual
                  records in Prism and a block of text cannot be reconciled to
                  them — so the Save button could never once have worked. Removed
                  rather than left as a trap that loses whatever was typed. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <h4 style={{ margin: 0 }}>Items</h4>
                {EDITABLE_STAGES.includes(selectedDeal.dealstage) && (
                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                    Prices editable here — names, quantities and adding or removing items on Quote Assessment
                  </span>
                )}
              </div>
              {renderModalParsedItems()}
            </div>

            {/* Deal Images */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={toggleDealImages}
                style={{ marginBottom: 'var(--space-2)' }}
              >
                {dealImagesExpanded ? 'Hide Images' : 'Show Images'}
              </button>
              {dealImagesExpanded && (
                <div>
                  {loadingDealImages ? (
                    <div className="loading-spinner" />
                  ) : dealImages.length > 0 ? (
                    <div className="jotform-images-grid">
                      {dealImages.map((url, i) => (
                        <div
                          key={i}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openLightbox(dealImages, i)}
                        >
                          <img
                            src={url}
                            alt={`Image ${i + 1}`}
                            className="jotform-image"
                            style={{ pointerEvents: 'none' }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No images found</p>
                  )}
                </div>
              )}
            </div>

            {selectedDeal.pricing_log_status && (
              <div style={{ marginBottom: 'var(--space-2)', fontSize: '0.85em', color: 'var(--color-text-muted)' }}>
                Pricing log status: <strong style={{ color: selectedDeal.pricing_log_status === 'completed' ? 'var(--color-success)' : selectedDeal.pricing_log_status === 'error' ? 'var(--color-error)' : 'var(--color-warning)' }}>{selectedDeal.pricing_log_status}</strong>
                {pricedButStale && <span> — stale: every item is already priced, nothing will pick this up. Use Mark priced.</span>}
              </div>
            )}

            {/* Pricing Log */}
            {(selectedDeal.claude_pricing_log || selectedDeal.pricing_log_status) && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className={`btn btn-sm ${pricingLogView === 'log' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setPricingLogView(pricingLogView === 'log' ? null : 'log'); setEditingPricingLog(false) }}
                  >
                    {pricingLogView === 'log' ? 'Hide Log' : 'Show Log'}
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{ background: '#7f1d1d', color: '#fca5a5', borderColor: '#991b1b' }}
                    onClick={forceReprice}
                    disabled={repricingDeal || queuedForPricing}
                  >
                    {repricingDeal ? 'Re-pricing...' : queuedForPricing ? 'Already queued' : 'Force Re-price'}
                  </button>
                  {pricedButStale && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={markPriced}
                      disabled={repricingDeal}
                      title="Every item already has a price in Prism but the pricing status is stuck on pending. Keep the prices and mark the quote as priced."
                    >
                      Mark priced
                    </button>
                  )}
                  {pricingLogView === 'log' && !editingPricingLog && EDITABLE_STAGES.includes(selectedDeal?.dealstage) && (
                    <>
                      <button className="btn btn-xs btn-secondary" onClick={() => { setEditingPricingLog(true); setPricingLogValue(selectedDeal.claude_pricing_log || '') }}>
                        Edit
                      </button>
                      {selectedDeal.claude_pricing_log && (
                        <button className="btn btn-xs btn-secondary" onClick={() => { navigator.clipboard.writeText(selectedDeal.claude_pricing_log); alert('Pricing log copied to clipboard!') }}>
                          Copy
                        </button>
                      )}
                    </>
                  )}
                  {pricingLogView === 'log' && editingPricingLog && (
                    <>
                      <button className="btn btn-xs btn-primary" onClick={savePricingLog} disabled={savingPricingLog}>
                        {savingPricingLog ? 'Saving...' : 'Save'}
                      </button>
                      <button className="btn btn-xs btn-secondary" onClick={() => { setEditingPricingLog(false); setPricingLogValue('') }} disabled={savingPricingLog}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>

                {/* Show Log view */}
                {pricingLogView === 'log' && (
                  editingPricingLog ? (
                    <textarea
                      className="form-input"
                      rows={12}
                      value={pricingLogValue}
                      onChange={e => setPricingLogValue(e.target.value)}
                      style={{ marginTop: 'var(--space-2)', fontFamily: 'monospace', fontSize: '0.75rem', width: '100%' }}
                      autoFocus
                    />
                  ) : (
                    <pre style={{
                      marginTop: 'var(--space-2)',
                      padding: 'var(--space-3)',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 4,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 300,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedDeal.claude_pricing_log}
                    </pre>
                  )
                )}

                <ImageQualityTable dealId={selectedDeal.id} />
              </div>
            )}

            {/* Warehouse Log */}
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                Warehouse Log
                <button
                  className="btn btn-xs btn-secondary"
                  onClick={loadTestResults}
                  disabled={loadingTestResults}
                >
                  {loadingTestResults ? 'Loading...' : testResults !== null ? 'Refresh' : 'Load Test Results'}
                </button>
              </label>
              {testResultsError && (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-error)', marginTop: 4 }}>{testResultsError}</div>
              )}
              {testResults === null && !loadingTestResults && !testResultsError && (
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: 4 }}>Click button to load test results</div>
              )}
              {testResults !== null && !testResults.results?.length && (
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: 4 }}>No test results recorded for this deal</div>
              )}
              {/* Prism (D4): one structured record per ITEM: outcome,
                  condition and notes. */}
              {testResults?.results?.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {testResults.results.map((r, idx) => (
                    <div key={r.quote_item_id || idx} style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      background: 'var(--color-bg-secondary)'
                    }}>
                      <div style={{
                        padding: '8px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid var(--color-border)',
                        background: r.outcome === 'issue' ? 'rgba(239,68,68,0.08)' : 'var(--color-bg-secondary)'
                      }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.name}</span>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          background: r.outcome === 'issue' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                          color: r.outcome === 'issue' ? '#dc2626' : '#16a34a'
                        }}>{r.outcome === 'issue' ? 'Issue' : 'OK'}</span>
                      </div>
                      <div style={{ padding: '8px 12px', fontSize: '0.82rem' }}>
                        {r.condition_after && (
                          <div style={{ marginBottom: 6 }}><strong>Condition after:</strong> {r.condition_after}</div>
                        )}
                        {r.defect_notes && (
                          <div style={{ marginBottom: 6, whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {r.defect_notes}</div>
                        )}
                        {(r.tested_at || r.tested_by) && (
                          <div style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
                            {r.tested_at ? new Date(r.tested_at).toLocaleDateString('en-GB') : ''}
                            {r.tested_at && r.tested_by ? ' · ' : ''}
                            {r.tested_by || ''}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Collection Link */}
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                Collection Link
                {isAdmin && (
                  <button
                    className="btn btn-xs btn-secondary"
                    onClick={regenerateCollectionToken}
                    disabled={regeneratingToken}
                  >
                    {regeneratingToken ? 'Regenerating...' : 'Regenerate Token'}
                  </button>
                )}
              </label>
              {selectedDeal.collection_link ? (
                <div>
                  <a href={selectedDeal.collection_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                    {selectedDeal.collection_link}
                  </a>
                  {selectedDeal.collection_link_state === 'expired' && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-error)', marginTop: 2 }}>
                      This token has expired — the link will not open for the customer. Chasing the quote renews it
                      without breaking their other links; Regenerate Token replaces it and breaks them.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: COLLECTION_LINK_ABSENCE[selectedDeal.collection_link_state]?.tone ?? 'var(--muted)' }}>
                  {COLLECTION_LINK_ABSENCE[selectedDeal.collection_link_state]?.text ?? 'Not set'}
                </div>
              )}
            </div>

            {/* Payout Correction Link — minted on demand and copied into the
                customer's ticket. Deliberately its own block, distinct from the
                Collection Link above: this one corrects payment details, it does
                not book a collection. */}
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                Payout Correction Link
                <button
                  className="btn btn-xs btn-secondary"
                  onClick={generatePayoutCorrectionLink}
                  disabled={generatingPayoutLink}
                >
                  {/* "Refresh", not "Regenerate": the worker asks Prism to ENSURE a
                      token (reuse a live one, renew one near expiry), so a second
                      click returns the same working link rather than rotating it
                      and killing a link the customer may already hold. */}
                  {generatingPayoutLink ? 'Generating...' : (payoutLink ? 'Refresh link' : 'Generate payout-correction link')}
                </button>
              </label>
              <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>
                Sends the customer to correct their payment details — not the collection link.
              </div>
              {payoutLinkError && (
                <div style={{ fontSize: '0.8rem', color: 'var(--color-error)', marginTop: 4 }}>
                  {payoutLinkError}
                </div>
              )}
              {payoutLink && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <a href={payoutLink} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                      {payoutLink}
                    </a>
                    <button className="btn btn-xs btn-secondary" onClick={copyPayoutLink} style={{ flexShrink: 0 }}>
                      {payoutLinkCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  {payoutExpiryLabel(payoutLinkExpiresAtMs) && (
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
                      {payoutExpiryLabel(payoutLinkExpiresAtMs)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Label Link */}
            {selectedDeal.label_link && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem' }}>Label Link</label>
                <div>
                  <a href={selectedDeal.label_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                    {selectedDeal.label_link}
                  </a>
                </div>
              </div>
            )}

            {/* Inventory File Download */}
            {selectedDeal.dealname && /\.xlsx?|\.csv/i.test(selectedDeal.dealname) && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem' }}>Inventory File</label>
                <div>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={async () => {
                      try {
                        const res = await fetch(`/api/deals/${selectedDeal.id}/inventory-file`, { credentials: 'include' })
                        if (!res.ok) {
                          const err = await res.json().catch(() => ({ error: 'Download failed' }))
                          throw new Error(err.error || 'Download failed')
                        }
                        const blob = await res.blob()
                        const url = URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        const disposition = res.headers.get('content-disposition') || ''
                        const match = disposition.match(/filename="(.+)"/)
                        a.download = match ? match[1] : 'inventory-file.xlsx'
                        a.click()
                        URL.revokeObjectURL(url)
                      } catch (e) {
                        alert(`Inventory file download failed: ${e.message}`)
                      }
                    }}
                  >
                    Download Inventory File
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
            <button className="btn btn-secondary" onClick={handleClose}>Close</button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>&times;</button>

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&#8249;</button>
            )}

            <img
              src={lightbox.images[lightbox.currentIndex]}
              alt={`Image ${lightbox.currentIndex + 1}`}
              className="lightbox-image"
            />

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-next" onClick={nextImage}>&#8250;</button>
            )}

            <div className="lightbox-counter">
              {lightbox.currentIndex + 1} / {lightbox.images.length}
            </div>

            {lightbox.images.length > 1 && (
              <div className="lightbox-thumbnails">
                {lightbox.images.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`lightbox-thumb ${idx === lightbox.currentIndex ? 'active' : ''}`}
                    onClick={() => setLightbox(prev => ({ ...prev, currentIndex: idx }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Create Ticket Modal */}
      {showTicketModal && ticketDeal && (
        <div className="modal-overlay">
          <div
            className="modal-content modal-large"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 800, width: '90%' }}
          >
            <div className="modal-header">
              <h2>Create Ticket</h2>
              <button className="modal-close" onClick={() => { setShowTicketModal(false); setTicketDeal(null); }}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>To</label>
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--color-bg)',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)'
                }}>
                  {ticketDeal.customer_name && <strong>{ticketDeal.customer_name}</strong>}
                  {ticketDeal.customer_name && ' '}
                  &lt;{ticketDeal.seller_email || ticketDeal.contact_email}&gt;
                </div>
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>Subject</label>
                <input
                  type="text"
                  className="form-input"
                  value={ticketSubject}
                  onChange={e => setTicketSubject(e.target.value)}
                  placeholder="Email subject..."
                  style={{ padding: '10px 14px' }}
                />
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>Message</label>
                <textarea
                  className="form-input"
                  rows={15}
                  value={ticketMessage}
                  onChange={e => setTicketMessage(e.target.value)}
                  placeholder="Type your message here..."
                  style={{ resize: 'vertical', minHeight: 300, padding: '12px 14px' }}
                />
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={getRewrite}
                    disabled={rewriteLoading || !ticketMessage.trim()}
                    style={{ fontSize: '0.8rem' }}
                  >
                    {rewriteLoading ? '✨ Rewriting...' : '✨ AI Rewrite'}
                  </button>
                  <OutboundPhotoPicker photos={ticketPhotos} />
                </div>
                <OutboundPhotoList photos={ticketPhotos} />
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                This will send an email to the customer and create a ticket in pending status.
              </p>

              {/* AI Rewrite Modal */}
              {showRewriteModal && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', zIndex: 10000,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={cancelRewrite}>
                  <div style={{
                    background: '#fff', borderRadius: 12, padding: 24,
                    maxWidth: 700, width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column'
                  }} onClick={e => e.stopPropagation()}>
                    <h3 style={{ margin: '0 0 16px 0' }}>✨ AI Rewrite</h3>
                    <div style={{
                      flex: 1, overflow: 'auto', padding: '16px',
                      background: '#f8f9f8', borderRadius: 8, color: '#222',
                      border: '1px solid var(--color-border)',
                      whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.6,
                      maxHeight: 400
                    }}>
                      {rewrittenText}
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                      <button className="btn btn-secondary" onClick={cancelRewrite}>Cancel</button>
                      <button className="btn btn-primary" onClick={acceptRewrite}>Accept</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowTicketModal(false); setTicketDeal(null); }}
                disabled={sendingTicket}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendTicket}
                disabled={sendingTicket || ticketPhotos.preparing || !ticketSubject || !ticketMessage}
              >
                {sendingTicket ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {returningItem && (
        <ReturnRequestDialog
          dealId={selectedDeal.quote_id}
          item={returningItem}
          onDone={() => {
            setReturningItem(null)
            if (onDealUpdated) onDealUpdated()
            onClose()
          }}
          onClose={() => setReturningItem(null)}
        />
      )}

      {repriceToast && (
        <div className={`feedback-toast feedback-toast-${repriceToast.type}`}>
          {repriceToast.message}
        </div>
      )}
    </>
  )
}

export default DealDetailModal
